import { useState, useEffect } from "react";
import {
  Typography,
  Button,
} from "@material-ui/core";

import { useStore } from "@store/store";
import Header from "@components/header";
import Loader from "@components/loader";
import {
  FETCH_BACK_DATED_EVENTS,
  FETCH_BACK_DATED_EVENTS_SUCCESS,
  FETCH_BACK_DATED_EVENTS_FAILURE,
  REVIEW_IMPACTED_CUSTOMERS,
  REVIEW_IMPACTED_CUSTOMERS_SUCCESS,
  REVIEW_IMPACTED_CUSTOMERS_FAILURE,
  REFRESH_IMPACTED_JOB_AND_INVOICE,
  REFRESH_IMPACTED_JOB_AND_INVOICE_SUCCESS,
  REFRESH_IMPACTED_JOB_AND_INVOICE_FAILURE,
} from "@utils/actionTypes";
import { rowsPerPageVal } from "@utils/constant";
import API from "@services/axios";
import TableListing from "./table-listing";
import { BackDatedEventsStyle } from "./style";
import { toast } from "react-toastify";

function BackDatedEvents() {
  const classes = BackDatedEventsStyle();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageVal);
  const [state, dispatch] = useStore();
  const [order, setOrder] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [selectedArray, setSelectedArray] = useState([]);
  const [dataTable, setData] = useState([]);
  const [mainCheck, setMainCheck] = useState(false);
  const [refreshRows, setRefreshRows] = useState({});

  const getBackDatedEvents = () => {
    const params = {
      page: page + 1,
      size: rowsPerPage,
      order: order !== "" ? order : "desc",
      orderBy: orderBy !== "" ? orderBy : "status",
    };
    dispatch({ type: FETCH_BACK_DATED_EVENTS });
    API.get("customers/backDatedEvents", { params })
      .then((response) => {
        const refreshRowMap = {};
        const rowsWithCheckFlag = response.data.data.rows.map(row => {
          refreshRowMap[row.id] = false;
          return {
            ...row,
            _rowChecked: false
          }
        });
        setData(rowsWithCheckFlag);
        setRefreshRows(refreshRowMap);

        dispatch({
          type: FETCH_BACK_DATED_EVENTS_SUCCESS,
          payload: { ...response.data.data, rows: rowsWithCheckFlag },
        });
      })
      .catch((error) => {
        dispatch({ type: FETCH_BACK_DATED_EVENTS_FAILURE, payload: error });
      });
  };

  useEffect(() => {
    getBackDatedEvents();
  }, [page, rowsPerPage, order, orderBy]);

  const updateBackDatedStatus = async (surchargeBackDateIds) => {
    dispatch({ type: REVIEW_IMPACTED_CUSTOMERS });
    try {
      const response = await API.put("customers/updateBackDatedStatus", { surchargeBackDateIds });
      dispatch({
        type: REVIEW_IMPACTED_CUSTOMERS_SUCCESS,
        payload: response.data.data,
      });

      toast.success("Back-dated events dismissed. No changes were made to jobs or invoices.");
      getBackDatedEvents();
    } catch (error) {
      dispatch({ type: REVIEW_IMPACTED_CUSTOMERS_FAILURE, payload: error });
      toast.error("Back-dated events dismiss failed");
    }
  }

  const handlePendingReviews = async () => {
    await updateBackDatedStatus(selectedArray);
    setSelectedArray([]);
  };

  const handleDismiss = async (item) => {
    await updateBackDatedStatus([item.id]);
  };

  const handleChange = (item) => {
    const updatedData = dataTable.map(row =>
      row.id === item.id ? { ...row, _rowChecked: !row._rowChecked } : row
    );

    setData(updatedData);

    const selectedIds = updatedData
      .filter(row => row.status === "Pending Review" && row._rowChecked)
      .map(row => row.id);

    setSelectedArray(selectedIds);

    const allChecked = updatedData
      .filter(row => row.status === "Pending Review")
      .every(row => row._rowChecked);

    setMainCheck(allChecked);
  };

  const handleMainChangeCheckBox = (e) => {
    const isChecked = e.target.checked;

    const updatedData = dataTable.map(item => {
      if (item.status === "Pending Review") {
        return { ...item, _rowChecked: isChecked };
      }
      return item; // leave others unchanged
    });

    setData(updatedData);

    // Only collect selected IDs for rows with "Pending Review"
    const selectedIds = isChecked
      ? updatedData.filter(item => item.status === "Pending Review").map(item => item.id)
      : [];

    setSelectedArray(selectedIds);
    setMainCheck(isChecked);
  };

  const refreshJobAndInvoiceImpacted = async (item) => {
    setRefreshRows((prev) => ({ ...prev, [item.id]: true }));
    dispatch({ type: REFRESH_IMPACTED_JOB_AND_INVOICE });
    try {
      const response = await API.put(`customers/${item.id}/refreshImpactedJobsAndInvoices`);
      dispatch({
        type: REFRESH_IMPACTED_JOB_AND_INVOICE_SUCCESS,
      });
      toast.success(response.data.message);
      getBackDatedEvents();
    } catch (error) {
      dispatch({ type: REFRESH_IMPACTED_JOB_AND_INVOICE_FAILURE });
      toast.error(error?.response?.data?.message);
      setRefreshRows((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSorting = (event, property) => {
    const isAsc = orderBy === property.sortTitle && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property.sortTitle);
  };
  return (
    <>
      <Header />
      <div className={classes.CustomerListingWrapper}>
        <Loader
          loading={
            state?.customer?.reviewingImpacted ||
            state?.customer?.loadingBackDatedEvents ||
            state?.customer?.refreshingImpactedJobAndInvoice
          }
        />
        <div className="dashboard-page wrapper">
          <div className="inner-page">
            <div className={classes.innerPageTopBlock}>
              <div className="left-block">
                <Typography variant="h1">Impacted Customers</Typography>
                <p className={classes.subTitle}>The following customers have jobs and/or draft invoices affected by the recent back-dated fuel surcharge change.</p>
              </div>
              <div className="right-block">
                <div className="right-block-inner">
                  <div>
                    <Button
                      onClick={() => handlePendingReviews()}
                      color="primary"
                      className="orange-btn primary-btn center-loading-indicator"
                      disabled={selectedArray.length === 0}
                    >
                      DISMISS PENDING REVIEW
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <TableListing
              count={state?.customer?.backDatedEventsData?.count}
              data={{
                ...(state?.customer?.backDatedEventsData || {}),
                rows: dataTable,
              }}
              handleDismiss={handleDismiss}
              handleChange={handleChange}
              handleMainChange={handleMainChangeCheckBox}
              refreshJobAndInvoiceImpacted={refreshJobAndInvoiceImpacted}
              _mainCheck={mainCheck}
              page={page}
              handleChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleSorting={(e, property) => handleSorting(e, property)}
              orderBy={orderBy}
              order={order}
              refreshRows={refreshRows}
            />
          </div>
        </div>
      </div>
    </>
  );
}
export default BackDatedEvents;
